import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { DeviceContextService } from '../device-context.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

interface DisplayCredentialItem {
    key: string;
    hasValue: boolean;
    valueLength: number;
    maskedValue: string;
    showValue?: boolean;
    actualValue?: string;
}

@Component({
    selector: 'app-cloud-credentials',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        TagModule,
        ButtonModule,
        TooltipModule,
        DialogModule,
        InputTextModule,
        PasswordModule,
        ConfirmDialogModule,
        MessageModule,
        SectionCardComponent,
        EmptyStateComponent
    ],
    templateUrl: './cloud-credentials.html',
    styleUrl: './cloud-credentials.scss',
    providers: [ConfirmationService]
})
export class CloudCredentials implements OnInit {
    private api = inject(CloudApiService);
    private ctx = inject(DeviceContextService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);

    items = signal<DisplayCredentialItem[]>([]);
    loading = signal(true);
    tunnelConnected = signal(false);
    errorMessage = signal<string | null>(null);

    // Dialog state
    dialogVisible = false;
    dialogMode: 'add' | 'edit' = 'add';
    editingItem: { key: string; value: string; confirmValue: string } = { key: '', value: '', confirmValue: '' };

    private deviceId: string | null = null;

    ngOnInit(): void {
        const device = this.ctx.device();
        if (device) {
            this.deviceId = device.id;
        }
        this.loadData();
    }

    loadData(): void {
        const device = this.ctx.device();
        if (!device) {
            this.errorMessage.set('Device not loaded');
            this.loading.set(false);
            return;
        }

        this.loading.set(true);
        this.errorMessage.set(null);

        this.api.getCredentials(device.id).then(response => {
            this.tunnelConnected.set(true);
            const displayItems: DisplayCredentialItem[] = (response.items || []).map(item => ({
                ...item,
                maskedValue: this.getMaskedValue(item.valueLength),
                showValue: false,
                actualValue: undefined
            }));
            this.items.set(displayItems);
            this.loading.set(false);
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.error || error?.message || 'Failed to load credentials');
            if (msg.includes('not connected via tunnel') || msg.includes('tunnel')) {
                this.tunnelConnected.set(false);
            } else {
                this.tunnelConnected.set(true);
                this.errorMessage.set(msg);
            }
            this.loading.set(false);
        });
    }

    getMaskedValue(length: number): string {
        if (length === 0) return '(empty)';
        return '•'.repeat(Math.min(length, 20));
    }

    showAddDialog(): void {
        this.dialogMode = 'add';
        this.editingItem = { key: '', value: '', confirmValue: '' };
        this.dialogVisible = true;
    }

    showEditDialog(item: DisplayCredentialItem): void {
        this.dialogMode = 'edit';
        this.editingItem = { key: item.key, value: '', confirmValue: '' };
        this.dialogVisible = true;
    }

    async toggleShowValue(item: DisplayCredentialItem): Promise<void> {
        const device = this.ctx.device();
        if (!device) return;

        if (item.showValue) {
            item.showValue = false;
            item.actualValue = undefined;
            this.items.update(items => [...items]);
        } else {
            try {
                const response = await this.api.getCredentialValue(device.id, item.key);
                item.actualValue = response.value;
                item.showValue = true;
                this.items.update(items => [...items]);
            } catch (error: any) {
                const msg = typeof error === 'string' ? error : (error?.message || 'Failed to retrieve credential value');
                this.messageService.add({
                    severity: 'error',
                    summary: 'Failed to Retrieve Credential',
                    detail: msg
                });
            }
        }
    }

    saveItem(): void {
        const device = this.ctx.device();
        if (!device) return;

        if (!this.editingItem.key.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Key is required' });
            return;
        }

        if (this.dialogMode === 'add' && !this.editingItem.value) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Value is required' });
            return;
        }

        // When editing, if both fields are empty, the user wants to keep the current value
        if (this.dialogMode === 'edit' && !this.editingItem.value && !this.editingItem.confirmValue) {
            this.dialogVisible = false;
            return;
        }

        if (this.editingItem.value !== this.editingItem.confirmValue) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Values do not match' });
            return;
        }

        this.api.setCredential(device.id, this.editingItem.key, this.editingItem.value).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Credential "${this.editingItem.key}" saved successfully`
            });
            this.dialogVisible = false;
            this.loadData();
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to save credential');
            this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: msg });
        });
    }

    confirmDelete(item: DisplayCredentialItem): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the credential "${item.key}"? This action cannot be undone.`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Delete',
            acceptButtonStyleClass: 'p-button-danger',
            rejectLabel: 'Cancel',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => this.deleteItem(item)
        });
    }

    deleteItem(item: DisplayCredentialItem): void {
        const device = this.ctx.device();
        if (!device) return;

        this.api.deleteCredential(device.id, item.key).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Credential "${item.key}" deleted successfully`
            });
            this.loadData();
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to delete credential');
            this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: msg });
        });
    }

    copyToClipboard(item: DisplayCredentialItem): void {
        const device = this.ctx.device();
        if (!device) return;

        const doCopy = (value: string) => {
            navigator.clipboard.writeText(value).then(() => {
                this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Credential value copied to clipboard' });
            }).catch(() => {
                this.messageService.add({ severity: 'error', summary: 'Failed to Copy', detail: 'Could not copy to clipboard' });
            });
        };

        if (item.actualValue) {
            doCopy(item.actualValue);
        } else {
            this.api.getCredentialValue(device.id, item.key).then(response => {
                doCopy(response.value);
            }).catch(error => {
                const msg = typeof error === 'string' ? error : (error?.message || 'Could not retrieve credential value');
                this.messageService.add({ severity: 'error', summary: 'Failed to Copy', detail: msg });
            });
        }
    }
}
